import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiHealthHost } from "../schema/api-health";
import { EndpointHistory } from "./EndpointHistory";
import { StatusChip } from "./StatusChip";

interface HostCardProps {
	host: ApiHealthHost;
}

export const HostCard: FunctionComponent<HostCardProps> = ({ host }) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState<string | null>(null);

	return (
		<section className="mb-6 overflow-hidden rounded-lg border border-gray-200 bg-white">
			<header className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
				<h4 className="font-mono text-sm text-gray-800">{host.host}</h4>
				<div className="flex items-center gap-3">
					<span className="text-xs text-gray-500">
						{t("apiHealth.healthy", { healthy: host.healthyCount, total: host.endpointCount })}
					</span>
					<StatusChip status={host.worstStatus} />
				</div>
			</header>

			<ul>
				{host.endpoints.map((endpoint) => {
					const isOpen = expanded === endpoint.contractKey;

					return (
						<li key={endpoint.contractKey} className="border-b border-gray-50 last:border-b-0">
							<button
								type="button"
								className="flex w-full items-center justify-between gap-3 px-4 py-2 text-left hover:bg-gray-50"
								onClick={() => setExpanded(isOpen ? null : endpoint.contractKey)}
							>
								<span className="min-w-0">
									<span className="block truncate text-sm text-gray-800">{endpoint.label}</span>
									<span className="block truncate font-mono text-xs text-gray-500">
										{endpoint.endpoint}
									</span>
								</span>

								<span className="flex shrink-0 items-center gap-3">
									<span className="hidden text-xs text-gray-400 sm:inline">
										{t(`apiHealth.monitoring.${endpoint.monitoring}`)}
									</span>
									<span className="text-xs text-gray-500">
										{endpoint.checkedAt
											? t("apiHealth.lastChecked", {
													when: endpoint.checkedAt.toLocaleString(),
												})
											: t("apiHealth.neverChecked")}
									</span>
									<StatusChip status={endpoint.status} />
								</span>
							</button>

							{isOpen && <EndpointHistory contractKey={endpoint.contractKey} />}
						</li>
					);
				})}
			</ul>
		</section>
	);
};
